import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Bot, Calendar, Shield, BarChart3, Clock, Users, Globe, ChevronDown, Check, ArrowRight, Headphones, Zap, Building2, Star, Mic, Brain, MessageCircle, Sparkles, Smartphone, Monitor, Tablet, Lock, Paperclip, Bell } from 'lucide-react';

const translations = {
  it: {
    nav: { features: 'Funzionalità', pricing: 'Prezzi', faq: 'FAQ', login: 'Accedi', register: 'Prova Gratis' },
    hero: {
      badge: 'La soluzione AI per Studi Medici e Cliniche',
      title: 'Le chiamate del tuo studio',
      titleHighlight: 'gestite in automatico.',
      subtitle: 'VoiceBook è l\'assistente AI vocale che gestisce tutte le chiamate del tuo studio medico o della tua clinica: prenota visite, gestisce disdette, riconosce i pazienti e tiene traccia di ogni conversazione. La tua segretaria riceve solo le chiamate che richiedono attenzione umana.',
      cta: 'Attiva per il tuo studio',
      ctaSub: '14 giorni gratis · Nessuna carta richiesta',
      login: 'Hai già un account?',
    },
    stats: [
      { value: '24/7', label: 'Gestione automatica' },
      { value: '100%', label: 'Chiamate tracciate' },
      { value: '15+', label: 'Lingue supportate' },
      { value: '70%', label: 'Carico segreteria ridotto' },
    ],
    features: {
      title: 'Progettato per studi medici, poliambulatori e cliniche',
      subtitle: 'Ogni funzionalità è pensata per le esigenze specifiche del settore sanitario',
      items: [
        { icon: 'Bot', title: 'Gestione Automatica Completa', desc: 'L\'AI gestisce prenotazioni, spostamenti e disdette in totale autonomia. Ogni chiamata viene trascritta e tracciata, nulla va perso.' },
        { icon: 'Calendar', title: 'Calendario Intelligente', desc: 'Gestione slot per reparto, medico e giorno della settimana. L\'AI propone solo orari realmente disponibili.' },
        { icon: 'Users', title: 'Multilingua — Pazienti Stranieri', desc: 'L\'AI comunica naturalmente anche con pazienti stranieri che non parlano bene la lingua. Niente piu\' malintesi o appuntamenti sbagliati per barriere linguistiche.' },
        { icon: 'Phone', title: 'Trasferimento Intelligente', desc: 'La segretaria riceve SOLO le chiamate che richiedono intervento umano: casi complessi, richieste particolari o quando il paziente lo chiede espressamente.' },
        { icon: 'Shield', title: 'GDPR Compliant', desc: 'Dati criptati, consensi tracciati, accesso controllato per ruolo. Conforme alla normativa europea sulla privacy.' },
        { icon: 'BarChart3', title: 'Tracciamento Totale', desc: 'Ogni chiamata registrata con trascrizione completa, durata, esito. Dashboard con statistiche, costi e report email automatici.' },
      ]
    },
    how: {
      title: 'Come funziona',
      steps: [
        { num: '1', title: 'Registrati', desc: 'Crea il tuo account partner in 2 minuti e configura la tua clinica.' },
        { num: '2', title: 'Configura', desc: 'Aggiungi reparti, medici e orari. Personalizza il comportamento dell\'AI.' },
        { num: '3', title: 'Attiva', desc: 'Collega il tuo numero telefonico e l\'AI inizia a rispondere alle chiamate.' },
      ]
    },
    ai: {
      title: 'L\'Intelligenza Artificiale che gestisce il tuo studio',
      subtitle: 'Non un semplice risponditore. Una segretaria AI che capisce, decide e agisce — anche con pazienti stranieri.',
      conversation: [
        { role: 'ai', text: 'Buongiorno Signor Marco, benvenuto al Centro Medico Roma. Come posso aiutarla?' },
        { role: 'patient', text: 'Vorrei prenotare una visita cardiologica.' },
        { role: 'ai', text: 'Certamente. Preferisce al mattino o nel pomeriggio?' },
        { role: 'patient', text: 'Al mattino, la prossima settimana.' },
        { role: 'ai', text: 'Le propongo martedì 8 luglio alle 9 e mezza oppure giovedì 10 alle 10. Quale preferisce?' },
        { role: 'patient', text: 'Martedì va benissimo.' },
        { role: 'ai', text: 'Perfetto, visita cardiologica confermata per martedì 8 luglio alle ore 9 e 30. Arrivederci!' },
      ],
      capabilities: [
        { icon: 'Mic', title: 'Voce naturale', desc: 'Tecnologia vocale proprietaria — i pazienti non distinguono l\'AI da una persona reale' },
        { icon: 'Brain', title: 'Comprende e decide', desc: 'Gestisce in autonomia il 90% delle chiamate. Trasferisce alla segretaria solo i casi che richiedono attenzione umana.' },
        { icon: 'Sparkles', title: 'Multilingua', desc: 'Comunica con pazienti stranieri che non parlano bene la lingua. Zero malintesi, zero appuntamenti sbagliati.' },
        { icon: 'MessageCircle', title: 'Tutto tracciato', desc: 'Ogni chiamata trascritta, ogni prenotazione registrata. Il medico e la segretaria hanno sempre il quadro completo.' },
      ]
    },
    chat: {
      title: 'Comunicazione Interna Sicura',
      subtitle: 'Medici e segreteria sempre connessi. Messaggi istantanei, condivisione file e ricette — tutto in un unico posto.',
      features: [
        { icon: 'MessageCircle', title: 'Chat di gruppo', desc: 'Un canale dedicato per tutta la clinica. Comunicazioni rapide senza email o telefonate.' },
        { icon: 'Lock', title: 'Messaggi privati', desc: 'Conversazioni riservate tra medico e segreteria. Dati sensibili protetti e conformi al GDPR.' },
        { icon: 'Paperclip', title: 'Condivisione file', desc: 'Invia ricette, referti e documenti direttamente in chat. Fino a 10MB per file.' },
        { icon: 'Bell', title: 'Notifiche in tempo reale', desc: 'Badge messaggi non letti e aggiornamento automatico. Non perdi mai una comunicazione importante.' },
      ]
    },
    pwa: {
      title: 'App per Medici e Pazienti',
      subtitle: 'VoiceBook sempre con te, su qualsiasi dispositivo. Senza scaricare nulla.',
      badge: 'Disponibile su tutti i dispositivi',
      doctor: {
        title: 'App Medici',
        desc: 'Il tuo ambulatorio digitale, sempre in tasca.',
        features: [
          'Calendario appuntamenti in tempo reale',
          'Gestione ricette mediche con firma digitale',
          'Chat interna con colleghi e segreteria',
          'Notifiche push per nuove prenotazioni',
          'Storico pazienti e visite precedenti',
          'Accesso sicuro con autenticazione 2FA',
        ]
      },
      patient: {
        title: 'App Pazienti',
        desc: 'Prenota, consulta e gestisci le tue visite in autonomia.',
        features: [
          'Prenotazione visite online 24/7',
          'Richiesta ricette direttamente dall\'app',
          'Storico appuntamenti e referti',
          'Reminder automatici via email',
          'Disdetta e spostamento con un tap',
          'Accesso alle ricette mediche (con OTP)',
        ]
      },
      advantages: [
        { title: 'Nessun download', desc: 'Funziona direttamente dal browser, nessun App Store necessario' },
        { title: 'Tutti i dispositivi', desc: 'Smartphone, tablet e PC — Android, iOS, Windows, Mac' },
        { title: 'Sempre aggiornata', desc: 'Aggiornamenti automatici, nessuna manutenzione per l\'utente' },
        { title: 'Installabile', desc: 'Aggiungi alla schermata Home per un\'esperienza nativa' },
      ]
    },
    pricing: {
      title: 'Piani e prezzi',
      subtitle: 'Paghi solo quello che usi. Nessun costo nascosto.',
      monthly: '/anno',
      popular: 'Più popolare',
      cta: 'Inizia la prova gratuita',
      ctaEnterprise: 'Contattaci',
      currency: '€',
      plans: [
        {
          name: 'Starter', price: '300', desc: 'Per studi medici singoli', period: '/anno per clinica',
          features: ['1 clinica inclusa', 'Setup gratuito', 'Traffico voce €0,10/min', 'Email conferma automatiche', 'Dashboard analytics', 'Wallet ricaricabile', 'Supporto email'],
        },
        {
          name: 'Professional', price: '300', desc: 'Per poliambulatori e gruppi', popular: true, period: '/anno per clinica',
          features: ['Fino a 10 cliniche', 'Setup gratuito', 'Traffico voce €0,10/min', 'Recall automatico €0,15/min', 'Lista d\'attesa smart', 'Reparti illimitati', 'Report email automatici', 'Supporto prioritario'],
        },
        {
          name: 'Enterprise', price: null, desc: 'Per grandi strutture sanitarie',
          features: ['Cliniche illimitate', 'Tariffe voce personalizzate', 'API personalizzate', 'SLA garantito', 'Account manager dedicato', 'Formazione on-site'],
        },
      ]
    },
    faq: {
      title: 'Domande frequenti',
      items: [
        { q: 'Come funziona la prova gratuita?', a: 'Hai 14 giorni per testare tutte le funzionalità senza limiti e senza carta di credito. Al termine puoi scegliere un piano o semplicemente non fare nulla.' },
        { q: 'Devo cambiare il mio numero di telefono?', a: 'No. VoiceBook si integra con il tuo numero esistente tramite la rete VoIP di TelcaVoIP. I pazienti continuano a chiamare lo stesso numero di sempre.' },
        { q: 'L\'AI parla davvero italiano naturale?', a: 'Sì, utilizza tecnologia vocale di ultima generazione con voci neurali di alta qualità. I pazienti spesso non si accorgono di parlare con un\'AI.' },
        { q: 'I dati dei pazienti sono al sicuro?', a: 'Assolutamente. La piattaforma è conforme al GDPR con crittografia dei dati, accesso basato su ruoli e audit log completo.' },
        { q: 'Posso personalizzare il comportamento dell\'AI?', a: 'Sì, puoi configurare il prompt dell\'AI, le regole di prenotazione, gli orari, i reparti e molto altro dal pannello di controllo.' },
      ]
    },
    footer: {
      cta: 'Pronto a rivoluzionare le prenotazioni del tuo studio?',
      ctaBtn: 'Inizia gratis',
      copy: '© 2026 VoiceBook by TelcaVoIP. Tutti i diritti riservati.',
      privacy: 'Privacy Policy',
    }
  },
  en: {
    nav: { features: 'Features', pricing: 'Pricing', faq: 'FAQ', login: 'Sign In', register: 'Try Free' },
    hero: {
      badge: 'The AI Solution for Medical Practices & Clinics',
      title: 'Your practice calls,',
      titleHighlight: 'managed automatically.',
      subtitle: 'VoiceBook is the AI voice assistant that handles all calls for your medical practice or clinic: books appointments, manages cancellations, recognizes patients and tracks every conversation. Your receptionist only handles calls requiring human attention.',
      cta: 'Activate for your practice',
      ctaSub: '14 days free · No credit card required',
      login: 'Already have an account?',
    },
    stats: [
      { value: '24/7', label: 'Automatic management' },
      { value: '100%', label: 'Calls tracked' },
      { value: '15+', label: 'Languages supported' },
      { value: '70%', label: 'Receptionist load reduced' },
    ],
    features: {
      title: 'Built for medical practices, clinics and healthcare',
      subtitle: 'Every feature is designed for the specific needs of the healthcare sector',
      items: [
        { icon: 'Bot', title: 'Complete Automatic Management', desc: 'AI handles bookings, rescheduling and cancellations autonomously. Every call is transcribed and tracked — nothing is lost.' },
        { icon: 'Calendar', title: 'Smart Calendar', desc: 'Slot management by department, doctor and day. AI only suggests actually available times.' },
        { icon: 'Users', title: 'Multilingual — Foreign Patients', desc: 'AI communicates naturally with patients who don\'t speak the local language perfectly. No more misunderstandings or wrong appointments.' },
        { icon: 'Phone', title: 'Smart Transfer', desc: 'Receptionist receives ONLY calls requiring human intervention: complex cases, special requests, or when the patient asks.' },
        { icon: 'Shield', title: 'GDPR Compliant', desc: 'Encrypted data, tracked consents, role-based access. Fully compliant with European privacy regulations.' },
        { icon: 'BarChart3', title: 'Total Tracking', desc: 'Every call recorded with full transcript, duration, outcome. Dashboard with statistics, costs and automatic email reports.' },
      ]
    },
    how: {
      title: 'How it works',
      steps: [
        { num: '1', title: 'Register', desc: 'Create your partner account in 2 minutes and set up your clinic.' },
        { num: '2', title: 'Configure', desc: 'Add departments, doctors and schedules. Customize the AI behavior.' },
        { num: '3', title: 'Activate', desc: 'Connect your phone number and the AI starts answering calls.' },
      ]
    },
    ai: {
      title: 'AI that runs your practice front desk',
      subtitle: 'Not a simple auto-responder. An AI receptionist that understands, decides and acts — even with foreign patients.',
      conversation: [
        { role: 'ai', text: 'Good morning Mr. Smith, welcome to City Medical Center. How can I help you?' },
        { role: 'patient', text: 'I\'d like to book a cardiology appointment.' },
        { role: 'ai', text: 'Of course. Would you prefer morning or afternoon?' },
        { role: 'patient', text: 'Morning, next week if possible.' },
        { role: 'ai', text: 'I can offer Tuesday July 8th at 9:30 AM or Thursday July 10th at 10 AM. Which works better?' },
        { role: 'patient', text: 'Tuesday is perfect.' },
        { role: 'ai', text: 'Done! Cardiology appointment confirmed for Tuesday July 8th at 9:30 AM. Goodbye!' },
      ],
      capabilities: [
        { icon: 'Mic', title: 'Natural voice', desc: 'Proprietary voice technology — patients can\'t tell they\'re talking to an AI' },
        { icon: 'Brain', title: 'Understands and decides', desc: 'Autonomously handles 90% of calls. Transfers to receptionist only cases requiring human attention.' },
        { icon: 'Sparkles', title: 'Multilingual', desc: 'Communicates with foreign patients and non-native speakers. Zero misunderstandings, zero wrong appointments.' },
        { icon: 'MessageCircle', title: 'Everything tracked', desc: 'Every call transcribed, every booking recorded. Doctor and receptionist always have the full picture.' },
      ]
    },
    chat: {
      title: 'Secure Internal Communication',
      subtitle: 'Doctors and staff always connected. Instant messages, file sharing and prescriptions — all in one place.',
      features: [
        { icon: 'MessageCircle', title: 'Group chat', desc: 'A dedicated channel for the whole clinic. Quick communications without emails or phone calls.' },
        { icon: 'Lock', title: 'Private messages', desc: 'Confidential conversations between doctor and staff. Sensitive data protected and GDPR compliant.' },
        { icon: 'Paperclip', title: 'File sharing', desc: 'Send prescriptions, reports and documents directly in chat. Up to 10MB per file.' },
        { icon: 'Bell', title: 'Real-time notifications', desc: 'Unread message badges and auto-refresh. Never miss an important message.' },
      ]
    },
    pwa: {
      title: 'Apps for Doctors and Patients',
      subtitle: 'VoiceBook always with you, on any device. No downloads needed.',
      badge: 'Available on all devices',
      doctor: {
        title: 'Doctor App',
        desc: 'Your digital practice, always in your pocket.',
        features: ['Real-time appointment calendar', 'Prescription management', 'Internal chat with colleagues', 'Push notifications for new bookings', 'Patient history and past visits', 'Secure access with 2FA']
      },
      patient: {
        title: 'Patient App',
        desc: 'Book, check and manage your visits independently.',
        features: ['Online booking 24/7', 'Request prescriptions from the app', 'Appointment history and reports', 'Automatic email reminders', 'Cancel or reschedule with one tap', 'Access prescriptions (with OTP)']
      },
      advantages: [
        { title: 'No download', desc: 'Works from your browser, no App Store needed' },
        { title: 'All devices', desc: 'Smartphone, tablet, PC — Android, iOS, Windows, Mac' },
        { title: 'Always updated', desc: 'Automatic updates, zero maintenance' },
        { title: 'Installable', desc: 'Add to Home Screen for native experience' },
      ]
    },
    pricing: {
      subtitle: 'Pay only for what you use. No hidden costs.',
      monthly: '/year',
      popular: 'Most popular',
      cta: 'Start free trial',
      ctaEnterprise: 'Contact us',
      currency: '€',
      plans: [
        {
          name: 'Starter', price: '300', desc: 'For single medical practices', period: '/year per clinic',
          features: ['1 clinic included', 'Free setup', 'Voice traffic €0.10/min', 'Automatic confirmation emails', 'Analytics dashboard', 'Rechargeable wallet', 'Email support'],
        },
        {
          name: 'Professional', price: '300', desc: 'For multi-specialty clinics', popular: true, period: '/year per clinic',
          features: ['Up to 10 clinics', 'Free setup', 'Voice traffic €0.10/min', 'Automatic recall €0.15/min', 'Smart waitlist', 'Unlimited departments', 'Automatic email reports', 'Priority support'],
        },
        {
          name: 'Enterprise', price: null, desc: 'For large healthcare facilities',
          features: ['Unlimited clinics', 'Custom voice rates', 'Custom APIs', 'Guaranteed SLA', 'Dedicated account manager', 'On-site training'],
        },
      ]
    },
    faq: {
      title: 'Frequently asked questions',
      items: [
        { q: 'How does the free trial work?', a: 'You get 14 days to test all features with no limits and no credit card. At the end you can choose a plan or simply do nothing.' },
        { q: 'Do I need to change my phone number?', a: 'No. VoiceBook integrates with your existing number via TelcaVoIP network. Patients keep calling the same number.' },
        { q: 'Does the AI really speak natural language?', a: 'Yes, it uses cutting-edge neural voice technology. Patients often don\'t realize they\'re talking to an AI.' },
        { q: 'Is patient data safe?', a: 'Absolutely. The platform is GDPR compliant with data encryption, role-based access and full audit logging.' },
        { q: 'Can I customize the AI behavior?', a: 'Yes, you can configure the AI prompt, booking rules, schedules, departments and much more from the control panel.' },
      ]
    },
    footer: {
      cta: 'Ready to revolutionize your practice bookings?',
      ctaBtn: 'Start free',
      copy: '© 2026 VoiceBook by TelcaVoIP. All rights reserved.',
      privacy: 'Privacy Policy',
    }
  },
  pl: {
    nav: { features: 'Funkcje', pricing: 'Cennik', faq: 'FAQ', login: 'Zaloguj', register: 'Wypróbuj' },
    hero: {
      badge: 'Rozwiązanie AI dla gabinetów i klinik medycznych',
      title: 'Połączenia Twojego gabinetu',
      titleHighlight: 'zarządzane automatycznie.',
      subtitle: 'VoiceBook to asystent głosowy AI, który zarządza wszystkimi połączeniami Twojego gabinetu lub kliniki: rezerwuje wizyty, obsługuje odwołania, rozpoznaje pacjentów i rejestruje każdą rozmowę. Recepcja obsługuje tylko połączenia wymagające ludzkiej uwagi.',
      cta: 'Aktywuj dla swojego gabinetu',
      ctaSub: '14 dni za darmo · Bez karty kredytowej',
      login: 'Masz już konto?',
    },
    stats: [
      { value: '24/7', label: 'Automatyczne zarządzanie' },
      { value: '100%', label: 'Połączenia śledzone' },
      { value: '15+', label: 'Obsługiwane języki' },
      { value: '70%', label: 'Mniej pracy recepcji' },
    ],
    features: {
      title: 'Zaprojektowany dla gabinetów, poliambulatorii i klinik',
      subtitle: 'Każda funkcja jest dopasowana do specyficznych potrzeb sektora medycznego',
      items: [
        { icon: 'Bot', title: 'Pełne automatyczne zarządzanie', desc: 'AI obsługuje rezerwacje, zmiany i odwołania samodzielnie. Każde połączenie jest transkrybowane i śledzone — nic nie ginie.' },
        { icon: 'Calendar', title: 'Inteligentny kalendarz', desc: 'Zarządzanie slotami wg oddziału, lekarza i dnia. AI proponuje tylko rzeczywiście dostępne terminy.' },
        { icon: 'Users', title: 'Wielojęzyczność — Pacjenci zagraniczni', desc: 'AI komunikuje się z pacjentami, którzy nie mówią biegle w lokalnym języku. Koniec nieporozumień i błędnych rezerwacji.' },
        { icon: 'Phone', title: 'Inteligentne przekierowanie', desc: 'Recepcja otrzymuje TYLKO połączenia wymagające interwencji ludzkiej: skomplikowane przypadki lub gdy pacjent o to prosi.' },
        { icon: 'Shield', title: 'Zgodność z RODO', desc: 'Szyfrowane dane, śledzone zgody, kontrola dostępu. Pełna zgodność z europejskimi przepisami o ochronie prywatności.' },
        { icon: 'BarChart3', title: 'Pełne śledzenie', desc: 'Każde połączenie z pełną transkrypcją, czasem trwania, wynikiem. Panel ze statystykami i automatycznymi raportami.' },
      ]
    },
    how: {
      title: 'Jak to działa',
      steps: [
        { num: '1', title: 'Zarejestruj się', desc: 'Utwórz konto partnera w 2 minuty i skonfiguruj klinikę.' },
        { num: '2', title: 'Skonfiguruj', desc: 'Dodaj oddziały, lekarzy i harmonogramy. Dostosuj zachowanie AI.' },
        { num: '3', title: 'Aktywuj', desc: 'Podłącz numer telefonu, a AI zacznie odbierać połączenia.' },
      ]
    },
    ai: {
      title: 'AI, która zarządza recepcją Twojego gabinetu',
      subtitle: 'Nie zwykły automat. Recepcjonistka AI, która rozumie, decyduje i działa — nawet z pacjentami zagranicznymi.',
      conversation: [
        { role: 'ai', text: 'Dzień dobry Panie Marku, witamy w Centrum Medycznym. Jak mogę Panu pomóc?' },
        { role: 'patient', text: 'Chciałbym umówić się na wizytę kardiologiczną.' },
        { role: 'ai', text: 'Oczywiście. Woli Pan rano czy po południu?' },
        { role: 'patient', text: 'Rano, w przyszłym tygodniu.' },
        { role: 'ai', text: 'Proponuję wtorek 8 lipca o 9:30 lub czwartek 10 lipca o 10:00. Który termin odpowiada?' },
        { role: 'patient', text: 'Wtorek jest idealny.' },
        { role: 'ai', text: 'Gotowe! Wizyta kardiologiczna potwierdzona na wtorek 8 lipca o 9:30. Do widzenia!' },
      ],
      capabilities: [
        { icon: 'Mic', title: 'Naturalny głos', desc: 'Autorska technologia głosowa — pacjenci nie rozpoznają, że rozmawiają z AI' },
        { icon: 'Brain', title: 'Rozumie i decyduje', desc: 'Samodzielnie obsługuje 90% połączeń. Przekierowuje do recepcji tylko przypadki wymagające ludzkiej uwagi.' },
        { icon: 'Sparkles', title: 'Wielojęzyczność', desc: 'Komunikuje się z pacjentami zagranicznymi. Zero nieporozumień, zero błędnych rezerwacji.' },
        { icon: 'MessageCircle', title: 'Wszystko śledzone', desc: 'Każde połączenie transkrybowane, każda rezerwacja zarejestrowana. Lekarz i recepcja zawsze mają pełny obraz.' },
      ]
    },
    chat: {
      title: 'Bezpieczna komunikacja wewnętrzna',
      subtitle: 'Lekarze i personel zawsze połączeni. Wiadomości błyskawiczne, udostępnianie plików i recept — wszystko w jednym miejscu.',
      features: [
        { icon: 'MessageCircle', title: 'Czat grupowy', desc: 'Dedykowany kanał dla całej kliniki. Szybka komunikacja bez e-maili i telefonów.' },
        { icon: 'Lock', title: 'Wiadomości prywatne', desc: 'Poufne rozmowy między lekarzem a recepcją. Dane wrażliwe chronione zgodnie z RODO.' },
        { icon: 'Paperclip', title: 'Udostępnianie plików', desc: 'Wysyłaj recepty, wyniki badań i dokumenty bezpośrednio w czacie. Do 10MB na plik.' },
        { icon: 'Bell', title: 'Powiadomienia w czasie rzeczywistym', desc: 'Plakietki nieprzeczytanych wiadomości i automatyczne odświeżanie. Nigdy nie przegapisz ważnej wiadomości.' },
      ]
    },
    pwa: {
      title: 'Aplikacje dla lekarzy i pacjentów',
      subtitle: 'VoiceBook zawsze z Tobą, na każdym urządzeniu. Bez pobierania.',
      badge: 'Dostępne na wszystkich urządzeniach',
      doctor: {
        title: 'Aplikacja Lekarza',
        desc: 'Twoja cyfrowa praktyka, zawsze w kieszeni.',
        features: ['Kalendarz wizyt w czasie rzeczywistym', 'Zarządzanie receptami', 'Wewnętrzny czat z kolegami', 'Powiadomienia push o nowych rezerwacjach', 'Historia pacjentów i wizyt', 'Bezpieczny dostęp z 2FA']
      },
      patient: {
        title: 'Aplikacja Pacjenta',
        desc: 'Rezerwuj, sprawdzaj i zarządzaj wizytami samodzielnie.',
        features: ['Rezerwacja online 24/7', 'Zamów receptę z aplikacji', 'Historia wizyt i wyniki', 'Automatyczne przypomnienia e-mail', 'Odwołanie lub zmiana jednym kliknięciem', 'Dostęp do recept (z OTP)']
      },
      advantages: [
        { title: 'Bez pobierania', desc: 'Działa z przeglądarki, bez App Store' },
        { title: 'Wszystkie urządzenia', desc: 'Smartfon, tablet, PC — Android, iOS, Windows, Mac' },
        { title: 'Zawsze aktualna', desc: 'Automatyczne aktualizacje, zero konserwacji' },
        { title: 'Instalowalna', desc: 'Dodaj do ekranu głównego jak natywna aplikacja' },
      ]
    },
    pricing: {
      title: 'Plany i cennik',
      subtitle: 'Płacisz tylko za to, co używasz. Bez ukrytych kosztów.',
      monthly: '/rok',
      popular: 'Najpopularniejszy',
      cta: 'Rozpocznij bezpłatnie',
      ctaEnterprise: 'Skontaktuj się',
      currency: 'zł',
      plans: [
        {
          name: 'Starter', price: '1.290', desc: 'Dla pojedynczych gabinetów', period: '/rok za klinikę',
          features: ['1 klinika w cenie', 'Bezpłatna konfiguracja', 'Ruch głosowy 0,45 zł/min', 'Automatyczne e-maile', 'Panel analityczny', 'Doładowywalny portfel', 'Wsparcie e-mail'],
        },
        {
          name: 'Professional', price: '1.290', desc: 'Dla poliambulatorii i grup', popular: true, period: '/rok za klinikę',
          features: ['Do 10 klinik', 'Bezpłatna konfiguracja', 'Ruch głosowy 0,45 zł/min', 'Automatyczny recall 0,65 zł/min', 'Inteligentna lista oczekujących', 'Nieograniczone oddziały', 'Automatyczne raporty e-mail', 'Priorytetowe wsparcie'],
        },
        {
          name: 'Enterprise', price: null, desc: 'Dla dużych placówek medycznych',
          features: ['Nieograniczone kliniki', 'Niestandardowe stawki głosowe', 'Niestandardowe API', 'Gwarantowane SLA', 'Dedykowany opiekun', 'Szkolenie na miejscu'],
        },
      ]
    },
    faq: {
      title: 'Często zadawane pytania',
      items: [
        { q: 'Jak działa bezpłatny okres próbny?', a: 'Masz 14 dni na przetestowanie wszystkich funkcji bez limitów i bez karty kredytowej. Po zakończeniu możesz wybrać plan lub po prostu nic nie robić.' },
        { q: 'Czy muszę zmienić numer telefonu?', a: 'Nie. VoiceBook integruje się z Twoim istniejącym numerem. Pacjenci dzwonią na ten sam numer co zawsze.' },
        { q: 'Czy AI naprawdę mówi naturalnie?', a: 'Tak, wykorzystuje najnowocześniejszą technologię głosową z wysokiej jakości głosami neuronowymi. Pacjenci często nie zdają sobie sprawy, że rozmawiają z AI.' },
        { q: 'Czy dane pacjentów są bezpieczne?', a: 'Absolutnie. Platforma jest zgodna z RODO z szyfrowaniem danych, kontrolą dostępu i pełnym logowaniem audytowym.' },
        { q: 'Czy mogę dostosować zachowanie AI?', a: 'Tak, możesz skonfigurować prompt AI, zasady rezerwacji, harmonogramy, oddziały i wiele więcej z panelu sterowania.' },
      ]
    },
    footer: {
      cta: 'Gotowy zrewolucjonizować rezerwacje w swoim gabinecie?',
      ctaBtn: 'Zacznij za darmo',
      copy: '© 2026 VoiceBook by TelcaVoIP. Wszelkie prawa zastrzeżone.',
      privacy: 'Polityka prywatności',
    }
  }
};

const iconMap = { Bot, Calendar, Phone, Shield, BarChart3, Users, Mic, Brain, Sparkles, MessageCircle };

const LangFlag = ({ lang }) => {
  const flags = { it: '🇮🇹', en: '🇬🇧', pl: '🇵🇱' };
  return <span className="text-lg">{flags[lang]}</span>;
};

export default function LandingPage() {
  const [lang, setLang] = useState('it');
  const [openFaq, setOpenFaq] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const t = translations[lang];

  useEffect(() => {
    const browserLang = navigator.language?.slice(0, 2);
    if (browserLang === 'pl') setLang('pl');
    else if (browserLang === 'en') setLang('en');
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-lg border-b border-slate-100 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-sky-500 rounded-xl flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>VoiceBook</span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => scrollTo('features')} className="text-sm text-slate-600 hover:text-sky-500 transition">{t.nav.features}</button>
            <button onClick={() => scrollTo('pricing')} className="text-sm text-slate-600 hover:text-sky-500 transition">{t.nav.pricing}</button>
            <button onClick={() => scrollTo('faq')} className="text-sm text-slate-600 hover:text-sky-500 transition">{t.nav.faq}</button>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:border-sky-300 transition text-sm" data-testid="lang-switcher">
                <LangFlag lang={lang} />
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-50">
                  {['it', 'en', 'pl'].map(l => (
                    <button key={l} onClick={() => { setLang(l); setLangOpen(false); }}
                      className={`flex items-center gap-2 px-4 py-2 text-sm w-full hover:bg-slate-50 ${lang === l ? 'bg-sky-50 text-sky-600' : ''}`}>
                      <LangFlag lang={l} /> {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Link to="/login" className="text-sm text-slate-600 hover:text-sky-500 transition hidden sm:block">{t.nav.login}</Link>
            <Link to="/register-partner" className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 transition" data-testid="nav-register-btn">{t.nav.register}</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-sky-50 border border-sky-200 rounded-full mb-6">
            <Zap className="w-4 h-4 text-sky-500" />
            <span className="text-sm text-sky-700 font-medium">{t.hero.badge}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'Manrope' }}>
            {t.hero.title}<br />
            <span className="text-sky-500">{t.hero.titleHighlight}</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            {t.hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            <Link to="/register-partner" className="w-full sm:w-auto px-8 py-3.5 bg-sky-500 text-white font-semibold rounded-xl hover:bg-sky-600 transition shadow-lg shadow-sky-500/25 flex items-center justify-center gap-2" data-testid="hero-cta">
              {t.hero.cta} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-slate-400">{t.hero.ctaSub}</p>
          <p className="text-sm text-slate-500 mt-3">{t.hero.login} <Link to="/login" className="text-sky-500 hover:underline">{t.nav.login}</Link></p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-slate-50 border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
          {t.stats.map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-sky-500 mb-1" style={{ fontFamily: 'Manrope' }}>{s.value}</div>
              <div className="text-sm text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>{t.features.title}</h2>
            <p className="text-slate-500 max-w-xl mx-auto">{t.features.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {t.features.items.map((f, i) => {
              const Icon = iconMap[f.icon] || Bot;
              return (
                <div key={i} className="p-6 rounded-2xl border border-slate-100 hover:border-sky-200 hover:shadow-lg transition-all group">
                  <div className="w-12 h-12 bg-sky-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-sky-100 transition">
                    <Icon className="w-6 h-6 text-sky-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12 sm:mb-16" style={{ fontFamily: 'Manrope' }}>{t.how.title}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {t.how.steps.map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-sky-500 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">{s.num}</div>
                <h3 className="text-lg font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Demo Section */}
      {t.ai && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-slate-900 to-slate-800" id="ai-demo">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-sky-500/10 border border-sky-500/20 rounded-full mb-6">
                <Brain className="w-4 h-4 text-sky-400" />
                <span className="text-sm text-sky-400 font-medium">AI-Powered</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope' }}>{t.ai.title}</h2>
              <p className="text-slate-400 max-w-xl mx-auto">{t.ai.subtitle}</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
              {/* Conversation Demo */}
              <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-5 sm:p-6 backdrop-blur">
                <div className="flex items-center gap-2 mb-5 pb-3 border-b border-slate-700">
                  <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs text-slate-400 font-medium tracking-wide uppercase">Chiamata in corso</span>
                </div>
                <div className="space-y-3">
                  {t.ai.conversation.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'ai' ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'ai'
                          ? 'bg-sky-500/15 text-sky-100 rounded-bl-sm'
                          : 'bg-slate-600/50 text-slate-200 rounded-br-sm'
                      }`}>
                        {msg.role === 'ai' && <Bot className="w-3.5 h-3.5 text-sky-400 inline mr-1.5 -mt-0.5" />}
                        {msg.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Capabilities */}
              <div className="grid sm:grid-cols-2 gap-4">
                {t.ai.capabilities.map((cap, i) => {
                  const capIcons = { Mic, Brain, Sparkles, MessageCircle };
                  const CapIcon = capIcons[cap.icon] || Sparkles;
                  return (
                    <div key={i} className="bg-slate-800/50 rounded-xl border border-slate-700 p-5 hover:border-sky-500/40 transition-all">
                      <div className="w-10 h-10 bg-sky-500/10 rounded-lg flex items-center justify-center mb-3">
                        <CapIcon className="w-5 h-5 text-sky-400" />
                      </div>
                      <h3 className="text-white font-semibold text-sm mb-1.5">{cap.title}</h3>
                      <p className="text-slate-400 text-xs leading-relaxed">{cap.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Chat Section */}
      {t.chat && (
        <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-white to-emerald-50/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full mb-6">
                <MessageCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-sm text-emerald-700 font-medium">Team Chat</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>{t.chat.title}</h2>
              <p className="text-slate-500 max-w-xl mx-auto">{t.chat.subtitle}</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {t.chat.features.map((feat, i) => {
                const chatIcons = { MessageCircle, Lock, Paperclip, Bell };
                const FeatIcon = chatIcons[feat.icon] || MessageCircle;
                return (
                  <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-emerald-300 transition-all group">
                    <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-colors">
                      <FeatIcon className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-1.5 text-sm">{feat.title}</h3>
                    <p className="text-slate-500 text-xs leading-relaxed">{feat.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* PWA Apps Section */}
      {t.pwa && (
        <section className="py-16 sm:py-24 px-4 sm:px-6" id="apps">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-50 border border-violet-200 rounded-full mb-6">
                <Smartphone className="w-4 h-4 text-violet-500" />
                <span className="text-sm text-violet-700 font-medium">{t.pwa.badge}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>{t.pwa.title}</h2>
              <p className="text-slate-500 max-w-xl mx-auto">{t.pwa.subtitle}</p>
            </div>

            {/* Doctor & Patient Apps */}
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-12">
              {/* Doctor App */}
              <div className="bg-gradient-to-br from-sky-50 to-white rounded-2xl border border-sky-200 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-sky-500 rounded-xl flex items-center justify-center">
                    <Monitor className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">{t.pwa.doctor.title}</h3>
                    <p className="text-sm text-slate-500">{t.pwa.doctor.desc}</p>
                  </div>
                </div>
                <ul className="space-y-2.5 mt-5">
                  {t.pwa.doctor.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-sky-500 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Patient App */}
              <div className="bg-gradient-to-br from-violet-50 to-white rounded-2xl border border-violet-200 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-violet-500 rounded-xl flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">{t.pwa.patient.title}</h3>
                    <p className="text-sm text-slate-500">{t.pwa.patient.desc}</p>
                  </div>
                </div>
                <ul className="space-y-2.5 mt-5">
                  {t.pwa.patient.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* PWA Advantages */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {t.pwa.advantages.map((adv, i) => {
                const advIcons = [Zap, Tablet, ArrowRight, Smartphone];
                const AdvIcon = advIcons[i] || Zap;
                return (
                  <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 text-center hover:border-sky-200 hover:shadow-md transition-all">
                    <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <AdvIcon className="w-5 h-5 text-sky-500" />
                    </div>
                    <h4 className="font-semibold text-sm mb-1">{adv.title}</h4>
                    <p className="text-xs text-slate-500">{adv.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Pricing */}
      <section id="pricing" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>{t.pricing.title}</h2>
            <p className="text-slate-500">{t.pricing.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {t.pricing.plans.map((plan, i) => (
              <div key={i} className={`rounded-2xl border p-6 sm:p-8 relative flex flex-col ${plan.popular ? 'border-sky-500 shadow-xl shadow-sky-500/10 scale-[1.02]' : 'border-slate-200'}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-sky-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" /> {t.pricing.popular}
                  </div>
                )}
                <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                <p className="text-sm text-slate-500 mb-4">{plan.desc}</p>
                <div className="mb-6">
                  {plan.price ? (
                    <span className="text-4xl font-bold" style={{ fontFamily: 'Manrope' }}>{t.pricing.currency || '€'}{plan.price}<span className="text-base font-normal text-slate-400">{plan.period || t.pricing.monthly}</span></span>
                  ) : (
                    <span className="text-2xl font-bold text-slate-600">Custom</span>
                  )}
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-sky-500 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register-partner"
                  className={`block text-center py-3 rounded-xl font-semibold transition ${plan.popular ? 'bg-sky-500 text-white hover:bg-sky-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                  {plan.price ? t.pricing.cta : t.pricing.ctaEnterprise}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-24 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12" style={{ fontFamily: 'Manrope' }}>{t.faq.title}</h2>
          <div className="space-y-3">
            {t.faq.items.map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition">
                  <span className="font-medium text-sm sm:text-base pr-4">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-sm text-slate-500 leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <Headphones className="w-12 h-12 text-sky-500 mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: 'Manrope' }}>{t.footer.cta}</h2>
          <Link to="/register-partner" className="inline-flex items-center gap-2 px-8 py-3.5 bg-sky-500 text-white font-semibold rounded-xl hover:bg-sky-600 transition shadow-lg shadow-sky-500/25" data-testid="footer-cta">
            {t.footer.ctaBtn} <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 border-t border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-sky-500 rounded-lg flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm text-slate-500">{t.footer.copy}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-sm text-slate-400 hover:text-sky-500 transition">{t.footer.privacy}</Link>
            <a href="https://telcavoip.ai" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-sky-500 transition">TelcaVoIP</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
